import { getEventSchema, isStandardEvent, ITEM_SCHEMA, GA4_EVENT_SCHEMAS } from './eventSchemas.js';

/**
 * Validierungs-Ergebnis
 */
class ValidationResult {
	constructor() {
		this.valid = true;
		this.errors = [];
		this.warnings = [];
	}

	addError(message) {
		this.valid = false;
		this.errors.push(message);
	}

	addWarning(message) {
		this.warnings.push(message);
	}

	hasIssues() {
		return this.errors.length > 0 || this.warnings.length > 0;
	}
}

/**
 * Event Validator für GA4 DataLayer Events
 *
 * Validiert Events gegen GA4 Standard-Schemas und gibt Fehler/Warnings zurück.
 * Kann im Development-Mode aktiviert werden um Event-Qualität sicherzustellen.
 */
export default class EventValidator {
	/**
	 * @param {Object} options - Validator Optionen
	 * @param {boolean} options.strictMode - Strenger Modus: auch Warnings als Errors behandeln
	 * @param {boolean} options.validateCustomEvents - Custom Events (nicht in Schema) auch validieren
	 * @param {boolean} options.logToConsole - Validierungsergebnisse in Console loggen
	 * @param {boolean} options.useConsoleError - flache console.error-Zeilen statt gruppierter Ausgabe
	 * @param {boolean} options.reportToSentry - Validierungsfehler an Sentry melden (via "log-error"-Event)
	 */
	constructor(options = {}) {
		this.strictMode = options.strictMode ?? false;
		this.validateCustomEvents = options.validateCustomEvents ?? true;
		this.logToConsole = options.logToConsole ?? true;
		this.useConsoleError = options.useConsoleError ?? false;
		this.reportToSentry = options.reportToSentry ?? false;
	}

	/**
	 * Validiert ein DataLayer Event
	 * @param {Object} event - Event-Objekt (von DataLayerEvent.toObject())
	 * @returns {ValidationResult} Validierungsergebnis
	 */
	validate(event) {
		const result = new ValidationResult();

		if (!event || typeof event !== 'object') {
			result.addError('Event must be an object');
			return result;
		}

		if (!event.event || typeof event.event !== 'string') {
			result.addError('Event must have a valid "event" property');
			return result;
		}

		const eventName = event.event;
		const schema = getEventSchema(eventName);

		// Custom Event (nicht im Schema definiert)
		if (!schema) {
			if (!this.validateCustomEvents) {
				return result; // Skip validation für custom events
			}

			result.addWarning(`"${eventName}" is not a standard GA4 event`);
			this._validateCustomEvent(event, result);

			// Im Strict Mode: Warnings werden zu Errors
			if (this.strictMode && result.warnings.length > 0) {
				result.warnings.forEach(warning => result.addError(warning));
				result.warnings = [];
			}

			this._logResult(eventName, result);
			return result;
		}

		// Standard Event validieren
		this._validateStandardEvent(event, schema, result);

		// Strict Mode ist deaktiviert - Warnings bleiben Warnings
		// Nur echte Errors (missing required, structure) werden geloggt

		this._logResult(eventName, result);

		return result;
	}

	/**
	 * Validiert ein Standard GA4 Event gegen sein Schema
	 * @private
	 */
	_validateStandardEvent(event, schema, result) {
		const eventName = event.event;
		const isEcommerceEvent = !!event.ecommerce;
		const params = isEcommerceEvent ? event.ecommerce : event;

		// Prüfe required Parameter
		if (schema.required && schema.required.length > 0) {
			for (const requiredParam of schema.required) {
				if (!(requiredParam in params) || params[requiredParam] === undefined || params[requiredParam] === null) {
					result.addError(`Missing required parameter: "${requiredParam}" for event "${eventName}"`);
				}
			}
		}

		// Prüfe Items wenn required
		if (schema.requiresItems) {
			if (!params.items || !Array.isArray(params.items)) {
				result.addError(`Event "${eventName}" requires an "items" array`);
			} else if (params.items.length === 0) {
				result.addError(`Event "${eventName}" requires at least one item in "items" array`);
			} else {
				// Validiere jedes Item
				params.items.forEach((item, index) => {
					this._validateItem(item, index, schema.itemRequired, result, eventName);
				});
			}
		}

		// Prüfe currency nur bei echten E-Commerce-Events (Schema kennt "currency").
		// Bei Nicht-E-Commerce-Events wie "click" stammt "value" aus einem
		// Formular-Attribut (z.B. value="4" oder value="RELEVANZ") und ist kein
		// monetärer Wert - dafür ist keine currency erforderlich.
		const schemaKnowsCurrency =
			(schema.required && schema.required.includes('currency')) ||
			(schema.optional && schema.optional.includes('currency'));
		if (schemaKnowsCurrency && params.value !== undefined && !params.currency) {
			result.addError(`Missing required "currency" for event "${eventName}" with "value"`);
		}

		// Prüfe unbekannte Parameter (nur Warning)
		const knownParams = [
			...(schema.required || []),
			...(schema.optional || []),
			'items', // Items ist immer erlaubt
			'currency' // Currency ist immer erlaubt bei E-Commerce
		];

		for (const param of Object.keys(params)) {
			if (!knownParams.includes(param) && !param.startsWith('_')) { // _ prefix für custom dimensions
				result.addWarning(`Unknown parameter "${param}" for event "${eventName}"`);
			}
		}
	}

	/**
	 * Validiert ein Custom Event (nicht im Schema)
	 * @private
	 */
	_validateCustomEvent(event, result) {
		const eventName = event.event;

		// Prüfe auf potentielle Tippfehler bei bekannten Events
		const suggestions = this._findSimilarEventNames(eventName);
		if (suggestions.length > 0) {
			result.addWarning(`Did you mean: ${suggestions.join(', ')}?`);
		}

		// Wenn Items vorhanden, validiere sie
		const params = event.ecommerce || event;
		if (params.items && Array.isArray(params.items)) {
			params.items.forEach((item, index) => {
				this._validateItem(item, index, null, result, eventName);
			});
		}
	}

	/**
	 * Validiert ein einzelnes Item
	 * @private
	 */
	_validateItem(item, index, requiredFields, result, eventName) {
		if (!item || typeof item !== 'object') {
			result.addError(`Item at index ${index} must be an object`);
			return;
		}

		// Prüfe requiredOne (item_id ODER item_name)
		if (!item.item_id && !item.item_name) {
			result.addError(`Item at index ${index} must have either "item_id" or "item_name"`);
		}

		// Prüfe event-spezifische required Item-Felder
		if (requiredFields && requiredFields.length > 0) {
			for (const field of requiredFields) {
				if (!(field in item) || item[field] === undefined || item[field] === null) {
					result.addWarning(`Item at index ${index} missing recommended field "${field}" for event "${eventName}"`);
				}
			}
		}

		// Prüfe Datentypen
		if (item.price !== undefined && typeof item.price !== 'number') {
			result.addError(`Item at index ${index}: "price" must be a number`);
		}

		if (item.quantity !== undefined && typeof item.quantity !== 'number') {
			result.addError(`Item at index ${index}: "quantity" must be a number`);
		}

		if (item.index !== undefined && typeof item.index !== 'number') {
			result.addError(`Item at index ${index}: "index" must be a number`);
		}
	}

	/**
	 * Findet ähnliche Event-Namen (für Tippfehler-Vorschläge)
	 * @private
	 */
	_findSimilarEventNames(eventName) {
		const standardEvents = Object.keys(GA4_EVENT_SCHEMAS);
		const similar = [];

		for (const standardEvent of standardEvents) {
			if (this._levenshteinDistance(eventName.toLowerCase(), standardEvent.toLowerCase()) <= 2) {
				similar.push(standardEvent);
			}
		}

		return similar;
	}

	/**
	 * Berechnet Levenshtein-Distanz zwischen zwei Strings
	 * @private
	 */
	_levenshteinDistance(str1, str2) {
		const matrix = [];

		for (let i = 0; i <= str2.length; i++) {
			matrix[i] = [i];
		}

		for (let j = 0; j <= str1.length; j++) {
			matrix[0][j] = j;
		}

		for (let i = 1; i <= str2.length; i++) {
			for (let j = 1; j <= str1.length; j++) {
				if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
					matrix[i][j] = matrix[i - 1][j - 1];
				} else {
					matrix[i][j] = Math.min(
						matrix[i - 1][j - 1] + 1,
						matrix[i][j - 1] + 1,
						matrix[i - 1][j] + 1
					);
				}
			}
		}

		return matrix[str2.length][str1.length];
	}

	/**
	 * Meldet das Validierungsergebnis - NUR echte Fehler, Warnings werden ignoriert.
	 *
	 * Sentry-Reporting (reportToSentry) und Konsolen-Ausgabe (logToConsole) sind
	 * unabhängig: In Production wird an Sentry gemeldet, ohne die Konsole zu fluten.
	 * @private
	 */
	_logResult(eventName, result) {
		// Nur Errors melden, keine Warnings!
		if (result.errors.length === 0) {
			return; // Keine Errors = nichts melden (auch keine Success-Message)
		}

		// An Sentry melden (unabhängig von der Konsolen-Ausgabe)
		if (this.reportToSentry) {
			for (const error of result.errors) {
				this._reportToSentry(`[DataLayer Validation] Event "${eventName}" - ${error}`);
			}
		}

		if (!this.logToConsole || typeof console === 'undefined') {
			return;
		}

		// Flache console.error-Zeilen (Staging)
		if (this.useConsoleError) {
			for (const error of result.errors) {
				console.error(`[DataLayer Validation] Event "${eventName}" - ${error}`);
			}
			return;
		}

		// Standard-Modus: Gruppierte Errors in der Konsole (nur Errors, keine Warnings!)
		console.group(`❌ Event "${eventName}" validation failed`);
		for (const error of result.errors) {
			console.error(`  ❌ ${error}`);
		}
		console.groupEnd();
	}

	/**
	 * Meldet eine Fehlermeldung an Sentry über das vorhandene "log-error"-Event.
	 *
	 * Das Error-Objekt wird hier im data-layer erzeugt, sodass sein Stacktrace auf
	 * das data-layer-Bundle zeigt. Dadurch routet die Sentry-Config das Event in das
	 * richtige Projekt ("datalayer") statt nach "merkur".
	 * @private
	 */
	_reportToSentry(message) {
		if (typeof window === 'undefined') {
			return;
		}

		try {
			const error = new Error(message);
			error.name = 'DataLayerValidationError';
			window.dispatchEvent(new CustomEvent('log-error', {
				bubbles: true,
				detail: { message: error }
			}));
		} catch (dispatchError) {
			if (typeof console !== 'undefined') {
				console.error('[DataLayer Validation] Failed to report to Sentry:', dispatchError);
			}
		}
	}

	/**
	 * Validiert ein DataLayer Event und wirft Error bei Fehlern (strictMode)
	 * @param {Object} event - Event-Objekt
	 * @throws {Error} Bei Validierungsfehlern
	 * @returns {ValidationResult}
	 */
	validateOrThrow(event) {
		const result = this.validate(event);

		if (this.strictMode && result.warnings.length > 0) {
			throw new Error(`Event validation failed:\n${[...result.errors, ...result.warnings].join('\n')}`);
		}

		if (result.errors.length > 0) {
			throw new Error(`Event validation failed:\n${result.errors.join('\n')}`);
		}

		return result;
	}
}

// Export ValidationResult für Testing
export { ValidationResult };
